import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { GoodunclesService, IssueImage, OwnerRef } from '@gooduncles/gu-app-schema';
import { Upload, message, notification } from 'antd';
import type { UploadFile } from 'antd';
import { UploadFileStatus } from 'antd/es/upload/interface';
import { FC, useCallback, useEffect, useState } from 'react';
import { ISSUE_IMAGE_BUCKET } from 'src/constants/storage.constants';
import { useAuth } from 'src/stores/auth-context';

import { errorObjectToString } from 'src/utils/common';
import { UploadImageResult, createImageFileDocuments, prepareImageForUpload } from 'src/utils/image-util';
import { deleteImageFile } from 'src/utils/image-util-deletion';
import { AuditLogger } from 'src/utils/audit-logger';

import styles from './IssueImageUploadItem.module.scss';

const auditLogger = AuditLogger.getInstance();

interface ImageUploadItemProps {
  owner: OwnerRef;
  images: {
    [key: string]: IssueImage;
  };
  onUploadSuccess: (result: UploadImageResult, index: number) => Promise<void>;
  onDeleteSuccess: (imageId: string) => Promise<void>;
  label?: string;
  maxCount?: number;
  disabled?: boolean;
}

const ImageUploadItem: FC<ImageUploadItemProps> = ({
  label = '이슈 사진',
  maxCount = 3,
  images,
  owner,
  onUploadSuccess,
  onDeleteSuccess,
  disabled,
}) => {
  const { user } = useAuth();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!user?._id) {
        notification.error({
          message: '로그인 후 이미지를 업로드해주세요.',
        });
        return false;
      }

      const prepResult = await prepareImageForUpload(file);
      const hasThumbnail = prepResult.thumbnailResult && prepResult.imageFileId;
      setFileList((prev) => [
        ...prev,
        {
          uid: prepResult.imageFileId,
          name: `image-${prepResult.imageFileId}`,
          status: hasThumbnail ? 'uploading' : 'error',
          error: hasThumbnail ? undefined : '썸네일 생성 실패',
          url: hasThumbnail
            ? prepResult.thumbnailResult?.previewUrl || URL.createObjectURL(file)
            : URL.createObjectURL(file),
          preview: URL.createObjectURL(file),
        },
      ]);
      if (!hasThumbnail) {
        return false;
      }

      try {
        const result = await createImageFileDocuments(ISSUE_IMAGE_BUCKET, file, prepResult, {
          service: GoodunclesService.Console,
          uploaderId: user._id,
          owner,
        });

        await onUploadSuccess(result, fileList.length);
        message.success('이미지를 업로드했습니다.');
      } catch (error) {
        setFileList((prev) =>
          prev.map((f) =>
            f.uid === prepResult.imageFileId ? { ...f, status: 'error', error: errorObjectToString(error) } : f,
          ),
        );
      }
      return false; // 자동 업로드 방지
    },
    [onUploadSuccess, owner, user?._id, fileList.length],
  );

  const handleRemove = async (file: UploadFile) => {
    const isErrorFile = fileList.find((f) => f.uid === file.uid)?.status === 'error';
    if (isErrorFile) {
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
      return;
    }

    try {
      setFileList((prev) => prev.map((f) => (f.uid === file.uid ? { ...f, status: 'uploading' } : f)));
      const deleteResult = await deleteImageFile(file.uid);
      await onDeleteSuccess(file.uid);
      if (deleteResult.errors.length > 0) {
        throw new Error(deleteResult.errors.map((error) => error.message).join(', '));
      }
      message.success('이미지를 삭제했습니다.');
    } catch (error) {
      setFileList((prev) =>
        prev.map((f) => (f.uid === file.uid ? { ...f, status: 'error', error: errorObjectToString(error) } : f)),
      );
      const description = errorObjectToString(error);
      auditLogger.log({
        action: 'DELETE',
        entity: 'issue',
        entityId: file.uid,
        summary: `이미지 삭제 실패\nerror: ${description}`,
        level: 'error',
      });
      notification.error({
        message: '이미지 삭제 실패',
        description,
      });
    }
  };

  const handlePreview = async (file: UploadFile) => {
    const imageUrl = file.preview || file.url || '';
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  };

  const uploadButton = (
    <button className={styles.uploadButton} type='button'>
      <PlusOutlined />
      <div>Upload</div>
    </button>
  );

  useEffect(() => {
    setFileList(
      Object.entries(images)
        .sort((a, b) => a[1].index - b[1].index)
        .map(([imageId, image]) => ({
          uid: imageId,
          name: `image-${imageId}`,
          status: 'done' as UploadFileStatus,
          url: image.thumbnailUrl || image.originalUrl,
          preview: image.originalUrl,
        })),
    );
  }, [images]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.label}>{label}</span>
      </div>

      <div className={styles.uploadArea}>
        <Upload
          listType='picture-card'
          fileList={fileList}
          beforeUpload={handleUpload}
          onRemove={handleRemove}
          onPreview={handlePreview}
          accept='image/*'
          disabled={disabled}
          iconRender={(file) => {
            if (file.status === 'uploading') {
              return <LoadingOutlined spin />;
            }
            return null;
          }}
          maxCount={maxCount}
        >
          {fileList.length >= maxCount ? null : uploadButton}
        </Upload>
      </div>
    </div>
  );
};

export default ImageUploadItem;
